import React from "react";
import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { T, cardSx, labelSx, chartTooltipStyle, axisTick, EmptyNote } from "./PlatformAnalyticsShared";
import FunnelChart from "./FunnelChart";

const headCellSx = {
  borderBottom: `1px solid ${T.border}`,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: T.dim,
  py: "10px",
  px: "16px",
};

const bodyCellSx = {
  py: "12px",
  px: "16px",
  fontSize: 12,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: T.fg,
  borderBottom: `1px solid ${T.border}`,
};

const captionSx = {
  fontSize: 11,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: T.dim,
  lineHeight: 1.45,
  mb: 1.5,
};

const FunnelsTab = ({ data }) => {
  const { application, supply, denial_reasons: denialReasons = [] } = data;
  const bySource = application?.by_source || [];

  return (
    <Stack spacing={3}>
      <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: T.dim, lineHeight: 1.5 }}>
        These funnels show how candidates and applications progress through each stage across the whole platform.
        Each bar is sized relative to the first stage, and the percentage is the share of that first stage still remaining, so you can see where the biggest drop-offs happen.
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" }, gap: "16px" }}>
        <Box sx={cardSx}>
          <Typography sx={{ ...labelSx, mb: 0.5 }}>Application funnel</Typography>
          <Typography sx={captionSx}>
            Forward journey of job applications, from created through open and accepted to an actual hire (reached a &quot;hired&quot; Kanban column).
          </Typography>
          <FunnelChart stages={application?.stages} color={T.primary} />
        </Box>
        <Box sx={cardSx}>
          <Typography sx={{ ...labelSx, mb: 0.5 }}>Candidate supply → apply</Typography>
          <Typography sx={captionSx}>
            Of the candidates we bring in, how many have a CV, get whitelisted, and ultimately apply to a job.
          </Typography>
          <FunnelChart stages={supply} color={T.teal} />
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" }, gap: "16px", alignItems: "start" }}>
        <Box sx={cardSx}>
          <Typography sx={{ ...labelSx, mb: 0.5 }}>Denial reasons</Typography>
          <Typography sx={captionSx}>
            Why applications get denied across the platform, ranked by how often each reason is recorded.
          </Typography>
          {denialReasons.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(160, denialReasons.length * 44)}>
              <BarChart data={denialReasons} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(140 8% 90%)" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="label" width={130} tick={axisTick} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Bar dataKey="count" name="Denials" fill={T.red} radius={[0, 4, 4, 0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyNote>No denial reasons recorded</EmptyNote>
          )}
        </Box>

        <Box sx={{ ...cardSx, p: 0, overflow: "hidden" }}>
          <Box sx={{ p: 2.5, pb: 1.5 }}>
            <Typography sx={{ ...labelSx, mb: 0.5 }}>Applications by source</Typography>
            <Typography sx={{ ...captionSx, mb: 0 }}>
              Where applications come from and how often each source is accepted, Talent Team (sent by us), CV Library (added from the CVthèque), or Organic (everything else).
            </Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Source", "Total", "Accepted", "Rate"].map((h) => (
                  <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {bySource.map((row) => {
                const rate = row.total > 0 ? Math.round((row.accepted / row.total) * 100) : 0;
                return (
                  <TableRow key={row.source} sx={{ "&:last-child td": { borderBottom: "none" } }}>
                    <TableCell sx={{ ...bodyCellSx, fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>{row.source}</TableCell>
                    <TableCell sx={bodyCellSx}>{row.total}</TableCell>
                    <TableCell sx={bodyCellSx}>{row.accepted}</TableCell>
                    <TableCell sx={bodyCellSx}>{rate}%</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Stack>
  );
};

export default FunnelsTab;
